import { useQuery } from '@tanstack/react-query'
import { Text } from '@viapeople/components'
import React from 'react'
import { trpc } from '../../../../utils/trpc'
import type { FormComponent } from '../../types'

interface ExternalDataConfig {
	fileId: string
	filename: string
	matchColumn: string
	columnsToDisplay: string[]
}

export function FormsExternalDataView({
	component,
	evaluateeExternalId,
	preview = false,
}: {
	component: FormComponent
	evaluateeExternalId?: string
	preview?: boolean
}): React.ReactElement {
	const custom = (component.custom || {}) as {
		label?: string
		labelColor?: string
		labelWeight?: 'regular' | 'semibold' | 'hidden' | null
		externalDataConfig?: ExternalDataConfig
	}

	const label = custom.label || 'External Data'
	const labelColor = custom.labelColor || undefined
	const labelWeight = custom.labelWeight || 'semibold'
	const config = custom.externalDataConfig
	const matchValue = evaluateeExternalId || ''
	const columns = config?.columnsToDisplay || []

	const { data, isLoading, error } = useQuery({
		...trpc.client.externalData.getRecordForUser.queryOptions({
			fileId: config?.fileId || '',
			matchColumn: config?.matchColumn || '',
			matchValue: matchValue,
		}),
		enabled: !!config?.fileId && !!config?.matchColumn && !!matchValue && !preview,
	})

	const recordData = data as Record<string, string> | null | undefined

	return (
		<div className='w-full flex flex-col gap-1'>
			{label && labelWeight !== 'hidden' && (
				<Text variant='small' weight={labelWeight} style={{ color: labelColor }}>
					{label}
				</Text>
			)}

			{!config?.fileId ? (
				<div className='px-4 py-3 bg-neutral-50 border border-dashed border-neutral-300 rounded-md text-center'>
					<Text variant='small' className='text-neutral-400'>
						Configure a data source in the property panel
					</Text>
				</div>
			) : preview ? (
				columns.length > 0 ? (
					<div className='bg-neutral-50 border border-neutral-200 rounded-md px-4 py-3'>
						<div className='grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5'>
							{columns.map((col) => (
								<React.Fragment key={col}>
									<Text variant='small' className='text-neutral-500 whitespace-nowrap'>
										{col}
									</Text>
									<Text variant='small' className='text-neutral-400 italic'>
										Sample data
									</Text>
								</React.Fragment>
							))}
						</div>
					</div>
				) : (
					<div className='px-4 py-3 bg-neutral-50 border border-dashed border-neutral-300 rounded-md text-center'>
						<Text variant='small' className='text-neutral-400'>
							Select columns to display
						</Text>
					</div>
				)
			) : (
				<>
					{isLoading && (
						<div className='flex items-center gap-2 px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-md'>
							<div className='animate-spin inline-block size-3.5 border-2 border-neutral-400 border-t-transparent rounded-full' />
							<Text variant='small' className='text-neutral-400'>
								Loading data...
							</Text>
						</div>
					)}
					{error && (
						<div className='px-4 py-3 bg-error-50 border border-error-200 rounded-md'>
							<Text variant='small' className='text-error-600'>
								Unable to load data
							</Text>
						</div>
					)}
					{!isLoading && !error && recordData === null && (
						<div className='px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-md'>
							<Text variant='small' className='text-neutral-400'>
								No matching record found
							</Text>
						</div>
					)}
					{!isLoading && !error && recordData && columns.length > 0 && (
						<div className='bg-neutral-50 border border-neutral-200 rounded-md px-4 py-3'>
							<div className='grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5'>
								{columns.map((col) => (
									<React.Fragment key={col}>
										<Text variant='small' className='text-neutral-500 whitespace-nowrap'>
											{col}
										</Text>
										<Text variant='small' className='text-neutral-900'>
											{recordData[col] || '\u2014'}
										</Text>
									</React.Fragment>
								))}
							</div>
						</div>
					)}
				</>
			)}
		</div>
	)
}
